import _ from 'lodash';
import React, { useMemo } from 'react';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/experimental-code-block/ui/accordion';
import {
  NestedAccordion,
  NestedAccordionsRoot,
} from 'react-scale/components/scalers/tasks/ThreadedInteraction/coding/experimental-code-block/ui/nested-accordions';
import cn from 'classnames';
import { Tooltip } from '@scale/scaleui';

const DEFAULT_OPEN_DEPTH = 3;

function isPrimitive(value: any) {
  return _.isNull(value) || _.isBoolean(value) || _.isNumber(value) || _.isString(value);
}

function RenderNull() {
  return (
    <span className={'text-[#ce9178] overflow-auto whitespace-pre-line break-words items-baseline'}>
      {'Null'}
    </span>
  );
}

function RenderBoolean({ value }: { value: boolean }) {
  return (
    <span
      className={
        'capitalize text-[#ce9178] overflow-auto whitespace-pre-line break-words items-baseline'
      }
    >
      {value.toString()}
    </span>
  );
}

function RenderNumber({ value }: { value: number }) {
  return (
    <span className="text-[#b5cea8] overflow-auto whitespace-pre-line break-words items-baseline">
      {value}
    </span>
  );
}

function RenderString({ value }: { value: string }) {
  if (value.startsWith('http') || value.startsWith('https')) {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#179fff',
        }}
        className="!text-[#179fff] overflow-auto whitespace-pre-line break-words items-baseline"
      >
        {value}
      </a>
    );
  }

  return (
    <span className="text-[#ce9178] overflow-auto whitespace-pre-line break-words items-baseline">
      {value}
    </span>
  );
}

function RenderArray({ value, depth = 0 }: { value: any[]; depth?: number }) {
  // Array of primitives
  if (value.every(isPrimitive)) {
    return (
      <span className="divide-x">
        {value.map((item, index) => (
          <span key={index} className="px-2 last:pr-0 first:pl-0">
            {renderValue(item)}
          </span>
        ))}
      </span>
    );
  }

  return (
    <NestedAccordion
      className="w-full border-b-0"
      defaultValue={[depth <= DEFAULT_OPEN_DEPTH ? `item-0` : '']}
    >
      {value.map((item, index) => (
        <AccordionItem key={index} value={`item-${index}`} className="w-full border-b-0 pb-0">
          <AccordionTrigger className="py-2 mb-1 pl-1 hover:bg-neutral-500 rounded">
            {`Item ${index + 1}`}
          </AccordionTrigger>
          <AccordionContent className="py-2 mb-3 -ml-7 pl-3 border-l border-neutral-500 overflow-scroll">
            {renderValue(item, depth + 1)}
          </AccordionContent>
        </AccordionItem>
      ))}
    </NestedAccordion>
  );
}

function RenderObject({ value, depth = 1 }: { value: Record<string, any>; depth?: number }) {
  return (
    <div>
      {Object.entries(value).map(([key, value], index) => {
        if (isPrimitive(value)) {
          return (
            <div
              key={index}
              style={{
                width: '120vw',
              }}
              className="pb-3 mb-1 pl-2 flex items-baseline"
            >
              <div className="whitespace-pre">{`${_.startCase(key)}:`}</div>
              &nbsp;
              <Tooltip
                asChild
                target={<div className="overflow-scroll">{renderValue(value)}</div>}
                text={<div className="break-words max-w-md">{value}</div>}
              />
            </div>
          );
        }

        return (
          <NestedAccordion
            className="w-full border-b-0"
            defaultValue={[depth <= DEFAULT_OPEN_DEPTH ? `item-0` : '']}
            key={index}
          >
            <AccordionItem value={`item-0`} className="w-full border-b-0 pb-0">
              <AccordionTrigger className="py-2 mb-1 pl-1 hover:bg-neutral-500 relative rounded text-neutral-0">
                {`${_.startCase(key)}:`}
              </AccordionTrigger>
              <AccordionContent className="py-2 mb-3 -ml-7 pl-3 border-l border-neutral-500 overflow-scroll">
                {renderValue(value, depth + 1)}
              </AccordionContent>
            </AccordionItem>
          </NestedAccordion>
        );
      })}
    </div>
  );
}

function renderValue(value: any, depth = 1) {
  if (_.isNull(value)) {
    return <RenderNull />;
  } else if (_.isBoolean(value)) {
    return <RenderBoolean value={value} />;
  } else if (_.isNumber(value)) {
    return <RenderNumber value={value} />;
  } else if (_.isString(value)) {
    return <RenderString value={value} />;
  } else if (_.isArray(value)) {
    return <RenderArray value={value} depth={depth} />;
  } else if (_.isObject(value)) {
    return <RenderObject value={value} depth={depth} />;
  } else {
    return null;
  }
}

interface JsonRendererProps {
  className?: string;
  disableYScroll?: boolean;
  content: string;
}

export const JsonRenderer = React.memo(
  ({ className, content, disableYScroll }: JsonRendererProps) => {
    const [parsedJson, isValidJson] = useMemo(() => {
      try {
        return [JSON.parse(content), true];
      } catch (e: any) {
        return [content, false];
      }
    }, [content]);

    return isValidJson ? (
      <NestedAccordionsRoot
        className={cn('px-3 pt-7 pb-2.5', !disableYScroll && 'overflow-y-auto', className)}
      >
        {renderValue(parsedJson)}
      </NestedAccordionsRoot>
    ) : null;
  },
);

JsonRenderer.displayName = 'JsonRenderer';
